import {useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {Button, Form, Modal} from 'react-bootstrap'
import {AvailabilityExceptionType} from '../core/_models'
import {
  CELLS_PER_DAY,
  cellIndexToMinutes,
  formatSlotRange,
  GridSlot,
  isAllDay,
  MINUTES_IN_DAY,
  minutesToLabel,
  normalizeSlots,
  SLOT_FILL_STYLE,
  SLOT_TYPE_HINTS,
  SLOT_TYPE_LABELS,
  STEP_MINUTES,
} from '../helpers/availability'
import {FdsSelect} from '../components/common/FdsSelect'

const modalsRoot = document.getElementById('root-modals') || document.body

// Ordered hardest-constraint first, the same order the paint palette uses.
// 'available' is dated-only: the weekly table says "available" by having no
// slot at all, so offering it there would post a type the endpoint rejects.
const WEEKLY_TYPES: AvailabilityExceptionType[] = ['unavailable', 'not_preferred', 'preferred']
const DATED_TYPES: AvailabilityExceptionType[] = [...WEEKLY_TYPES, 'available']

const DEFAULT_START = 9 * 60
const DEFAULT_END = 17 * 60

interface Draft {
  // Identity for React and for the remove button. Index keys would reuse a
  // row's inputs for the NEXT row when one in the middle is removed.
  key: number
  slot_type: AvailabilityExceptionType
  start: number
  end: number
  note: string
}

let nextKey = 1
const draftFromSlot = (slot: GridSlot): Draft => ({
  key: nextKey++,
  slot_type: slot.slot_type,
  start: slot.start_minute,
  end: slot.end_minute,
  note: slot.note ?? '',
})

const blankDraft = (): Draft => ({
  key: nextKey++,
  // The hardest constraint, which is also the paint grid's default tool. A
  // softer default would be the one state nobody needs to record: an
  // unrecorded day already means available.
  slot_type: 'unavailable',
  start: DEFAULT_START,
  end: DEFAULT_END,
  note: '',
})

interface Props {
  show: boolean
  /** Who and which day — rendered as the modal's title. */
  title: string
  /** Dated mode: 'available' becomes offerable and the hints talk about one date. */
  dated: boolean
  /** The day's own entries, which is what this modal edits. */
  entries: GridSlot[]
  /**
   * Weekly rules the open date has not overridden. Read-only here: a rule
   * belongs to every such weekday, so editing it from one date would quietly
   * change the others. Shown because it IS what this date falls back to.
   */
  inherited?: GridSlot[]
  use12Hour?: boolean
  saving?: boolean
  onCancel: () => void
  /** The day's complete new list — an empty one clears the day. */
  onSave: (slots: GridSlot[]) => void
}

/**
 * The editor behind one cell of the availability board.
 *
 * A cell is a whole day, so this edits a LIST: every entry registered for that
 * day, each with its own type, hours and note. Saving sends the list as the
 * day's complete contents — which is also how a day is cleared, by saving it
 * empty — because both endpoints behind it replace rather than patch.
 *
 * Times are 30-minute dropdowns rather than `<input type='time'>`: the grid's
 * resolution is half an hour, and an end of 24:00 is a real value a time input
 * cannot hold (its maximum is 23:59), which is exactly how a day-long block is
 * expressed.
 *
 * Overlaps are not refused. They are resolved on save the way the server
 * resolves them (`normalizeSlots`: a later entry wins the minutes it covers),
 * so what a save stores is what the modal showed.
 */
export const AvailabilityCellModal = ({
  show,
  title,
  dated,
  entries,
  inherited,
  use12Hour = false,
  saving,
  onCancel,
  onSave,
}: Props) => {
  const [drafts, setDrafts] = useState<Draft[]>([])
  const [error, setError] = useState<string | null>(null)

  // Seeded on open, not on every `entries` change: a refetch landing while the
  // modal is open must not throw away what is being typed into it.
  useEffect(() => {
    if (!show) return
    setDrafts(entries.map(draftFromSlot))
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const types = dated ? DATED_TYPES : WEEKLY_TYPES

  // 00:00–23:30 to start, 00:30–24:00 to end. 24:00 is the end of the day, not
  // midnight of the next one, and minutesToLabel already renders it that way.
  const startOptions = useMemo(
    () => Array.from({length: CELLS_PER_DAY}, (_, index) => cellIndexToMinutes(index)),
    []
  )
  const endOptions = useMemo(
    () => startOptions.map((minute) => minute + STEP_MINUTES),
    [startOptions]
  )

  const update = (key: number, patch: Partial<Draft>) =>
    setDrafts((current) =>
      current.map((draft) => (draft.key === key ? {...draft, ...patch} : draft))
    )

  const handleSave = () => {
    const invalid = drafts.find((draft) => draft.end <= draft.start)
    if (invalid) {
      // Not silently split across midnight: a slot belongs to one day, so an
      // overnight stretch is two entries (or "All day"), and the user is the
      // one who knows which they meant.
      setError(
        'An entry has to end after it starts. For hours running over midnight, add one entry on each day.'
      )
      return
    }
    setError(null)
    onSave(
      normalizeSlots(
        drafts.map((draft) => ({
          // The caller owns the day: this modal edits one cell, and the page
          // knows whether that cell is a weekday or a date.
          day_of_week: 0,
          slot_type: draft.slot_type,
          start_minute: draft.start,
          end_minute: draft.end,
          note: draft.note.trim() || null,
        }))
      )
    )
  }

  return createPortal(
    <Modal
      id='fds_modal_availability_cell'
      tabIndex={-1}
      dialogClassName='modal-dialog modal-dialog-centered mw-850px'
      show={show}
      onHide={onCancel}
      backdrop={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <span className='fs-5'>{title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!!inherited?.length && (
          <div
            className='alert alert-light-primary d-flex flex-column py-3 px-4 mb-5'
            data-testid='availability-cell-inherited'
          >
            <span className='fs-8 fw-semibold text-gray-700'>
              Still in force from the weekly pattern
            </span>
            {inherited.map((slot, index) => (
              <span key={index} className='fs-7 text-gray-800 mt-1'>
                <span
                  aria-hidden='true'
                  className='d-inline-block rounded me-2 align-middle'
                  style={{width: 12, height: 12, ...SLOT_FILL_STYLE[slot.slot_type]}}
                />
                {SLOT_TYPE_LABELS[slot.slot_type]} ·{' '}
                {isAllDay(slot) ? 'all day' : formatSlotRange(slot, use12Hour)}
              </span>
            ))}
            <span className='fs-8 text-gray-600 mt-2'>
              An entry below overrides only the hours it covers, and only on this date.
            </span>
          </div>
        )}

        {drafts.length === 0 ? (
          <div
            className='text-gray-500 fs-7 py-4 text-center'
            data-testid='availability-cell-empty'
          >
            {dated
              ? 'Nothing registered for this date — the weekly pattern applies.'
              : 'Nothing registered — available all day.'}
          </div>
        ) : (
          <div className='d-flex flex-column gap-3'>
            {drafts.map((draft) => {
              const allDay = draft.start === 0 && draft.end === MINUTES_IN_DAY
              return (
                <div
                  key={draft.key}
                  className='border border-gray-300 rounded px-3 py-3'
                  data-testid='availability-cell-row'
                >
                  {/* One control line, every field the same height so the
                      baselines line up. The type's plain-language hint is a
                      line of its own underneath rather than a caption under
                      the select: in the row it made that one column taller
                      than the rest and knocked everything out of alignment. */}
                  <div className='d-flex flex-wrap align-items-center gap-3'>
                    <span
                      aria-hidden='true'
                      className='d-block rounded flex-shrink-0'
                      style={{width: 14, height: 14, ...SLOT_FILL_STYLE[draft.slot_type]}}
                    />
                    <FdsSelect<AvailabilityExceptionType>
                      ariaLabel='Entry type'
                      value={draft.slot_type}
                      onChange={(slot_type) => update(draft.key, {slot_type})}
                      options={types.map((type) => ({value: type, label: SLOT_TYPE_LABELS[type]}))}
                    />

                    <span className='d-flex align-items-center gap-2'>
                      <span className='fs-8 text-gray-600'>from</span>
                      <FdsSelect<number>
                        ariaLabel='From'
                        disabled={allDay}
                        value={draft.start}
                        onChange={(start) => update(draft.key, {start})}
                        options={startOptions.map((minute) => ({
                          value: minute,
                          label: minutesToLabel(minute, use12Hour),
                        }))}
                      />
                      <span className='fs-8 text-gray-600'>to</span>
                      <FdsSelect<number>
                        ariaLabel='To'
                        disabled={allDay}
                        value={draft.end}
                        onChange={(end) => update(draft.key, {end})}
                        options={endOptions.map((minute) => ({
                          value: minute,
                          label: minutesToLabel(minute, use12Hour),
                        }))}
                      />
                    </span>

                    <Form.Check
                      type='checkbox'
                      className='m-0'
                      id={`availability-all-day-${draft.key}`}
                      label={<span className='fs-8 text-gray-700 text-nowrap'>All day</span>}
                      checked={allDay}
                      // A derived state, not a third stored field: "all day" IS
                      // 00:00–24:00, and keeping a flag beside the hours lets
                      // the two disagree. Unticking restores the default hours
                      // rather than leaving an unusable 00:00–00:00 row.
                      onChange={(event) =>
                        update(
                          draft.key,
                          event.target.checked
                            ? {start: 0, end: MINUTES_IN_DAY}
                            : {start: DEFAULT_START, end: DEFAULT_END}
                        )
                      }
                    />

                    <Form.Control
                      size='sm'
                      type='text'
                      aria-label='Note'
                      className='flex-grow-1'
                      style={{minWidth: 150}}
                      value={draft.note}
                      placeholder='Note (optional)'
                      onChange={(event) => update(draft.key, {note: event.target.value})}
                    />

                    <Button
                      size='sm'
                      variant='light-danger'
                      className='flex-shrink-0'
                      aria-label='Remove entry'
                      title='Remove this entry'
                      onClick={() =>
                        setDrafts((current) => current.filter((row) => row.key !== draft.key))
                      }
                    >
                      <i className='bi bi-trash fs-7' aria-hidden='true' />
                    </Button>
                  </div>

                  <span className='fs-8 text-gray-500 d-block mt-2'>
                    {SLOT_TYPE_HINTS[draft.slot_type]}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        <Button
          size='sm'
          variant='light-primary'
          className='mt-4'
          data-testid='availability-cell-add'
          onClick={() => setDrafts((current) => [...current, blankDraft()])}
        >
          <i className='bi bi-plus-lg fs-8 me-2' aria-hidden='true' />
          Add an entry
        </Button>

        {error && (
          <div className='alert alert-danger fs-7 mt-4 mb-0' role='alert'>
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-between'>
        <Button
          variant='light-danger'
          size='sm'
          // Emptying the list and saving IS the clear — the endpoints replace
          // rather than patch — so this is the same path, not a second one.
          disabled={saving || !drafts.length}
          data-testid='availability-cell-clear'
          onClick={() => {
            setDrafts([])
            setError(null)
          }}
        >
          Clear the day
        </Button>
        <span className='d-flex gap-2'>
          <Button variant='secondary' size='sm' disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant='primary'
            size='sm'
            disabled={saving}
            data-testid='availability-cell-save'
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </span>
      </Modal.Footer>
    </Modal>,
    modalsRoot
  )
}
